import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { PageIntro, Reveal, Shell } from "@/components/layout/Shell";

export const Lightbox = ({ photos, index, onClose, onMove }) => {
  const onKey = useCallback(e => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowRight") onMove(1);
    if (e.key === "ArrowLeft") onMove(-1);
  }, [onClose, onMove]);
  useEffect(() => { window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, [onKey]);
  const p = photos[index];
  if (!p) return null;
  return (
    <motion.div className="lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="lightbox">
      <button className="lb-close" onClick={onClose} aria-label="Close" data-testid="lightbox-close"><X size={20} /></button>
      <button className="lb-nav prev" onClick={e => { e.stopPropagation(); onMove(-1); }} aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>
      <motion.img key={p.id || index} src={mediaUrl(p.url)} alt={p.caption || ""} onClick={e => e.stopPropagation()} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} data-testid="lightbox-image" />
      <button className="lb-nav next" onClick={e => { e.stopPropagation(); onMove(1); }} aria-label="Next" data-testid="lightbox-next"><ChevronRight size={22} /></button>
      <div className="lb-caption" data-testid="lightbox-counter">{p.caption && <span>{p.caption}</span>}<small>{index + 1} / {photos.length}</small></div>
    </motion.div>
  );
};

export default function GalleryPage() {
  const { albumId } = useParams();
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (albumId) api(`/public/albums/${albumId}`).then(setAlbum).catch(() => setAlbum({ missing: true }));
    else api("/public/gallery").then(d => setAlbums(d.items || [])).catch(() => {});
  }, [albumId]);

  const photos = album?.photos || [];
  const move = d => setOpen(i => (i + d + photos.length) % photos.length);

  if (albumId) {
    return (
      <Shell>
        <main className="inner-page">
          <div className="container page-intro">
            <Reveal>
              <Link to="/gallery" className="text-link" style={{ marginTop: 0, marginBottom: 20 }} data-testid="album-back"><ArrowLeft size={14} /> All albums</Link>
              <br />
              <span className="eyebrow">DCEA / Gallery</span>
              <h1>{album?.title || "Album"}</h1>
              <p>{album?.description || album?.count || ""}</p>
            </Reveal>
          </div>
          <div className="container photo-grid">
            {album?.missing ? <div className="empty-state">Album not found.</div> : photos.length ? photos.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.05}>
                <button className="photo-tile" onClick={() => setOpen(i)} data-testid={`photo-${i}`}>
                  <img src={mediaUrl(p.url)} alt={p.caption || album.title} loading="lazy" />
                  {p.caption && <span>{p.caption}</span>}
                </button>
              </Reveal>
            )) : <div className="empty-state" data-testid="album-empty">Photos for this album are coming soon.</div>}
          </div>
          <AnimatePresence>{open !== null && <Lightbox photos={photos} index={open} onClose={() => setOpen(null)} onMove={move} />}</AnimatePresence>
        </main>
      </Shell>
    );
  }

  return (
    <Shell>
      <main className="inner-page">
        <PageIntro crumb="DCEA / Gallery" title={<>In the <em className="gold-text">field</em></>} description="Moments from association events, workshops and site visits." />
        <div className="container page-grid">
          {albums.length ? albums.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <Link to={`/gallery/${a.id}`} className="listing-card album-card" data-testid={`gallery-item-${i}`}>
                <img src={mediaUrl(a.cover)} alt={a.title} />
                <div>
                  <span className="card-kicker"><Images size={11} /> {a.photos?.length ? `${a.photos.length} photos` : a.count || "Album"}</span>
                  <h2>{a.title}</h2>
                  <p>{a.description || "Open the album to browse photos."}</p>
                </div>
              </Link>
            </Reveal>
          )) : <div className="empty-state" data-testid="gallery-empty-state">No albums published yet.</div>}
        </div>
      </main>
    </Shell>
  );
}
